/**
 * Tree-related facet helper functions for building hierarchical category structures.
 * Extracted from VoyagerSearchService.ts.
 */

import { logWarn } from '../../../../utils/logger';
import type { VoyagerFacetValueCategoryConfig } from './VoyagerSearchService.types';
import type { FilterItem, FilterValueCategory } from '../models/search-result';

export interface TreeNsdiHierarchyView {
  valueCategories: FilterValueCategory[];
  filters: FilterItem[];
  ungroupedFilters?: FilterItem[];
  matchedHierarchyPath?: string;
  emptyMessage?: string;
}

export const matchesFacetValueCategory = (
  item: FilterItem,
  category: VoyagerFacetValueCategoryConfig,
): boolean => {
  if (category.values?.includes(item.id)) {
    return true;
  }

  if (category.startsWith?.some((prefix) => item.id.startsWith(prefix))) {
    return true;
  }

  if (category.contains?.some((fragment) => item.id.includes(fragment))) {
    return true;
  }

  if (category.regex) {
    try {
      return new RegExp(category.regex, 'i').test(item.id);
    } catch (error) {
      logWarn('VoyagerSearchService: Invalid value category regex', error);
    }
  }

  return false;
};

export const groupFacetValues = (
  filters: FilterItem[],
  valueCategories: VoyagerFacetValueCategoryConfig[] = [],
  assignedFilterIds: Set<string> = new Set(),
  parentPath: string = '',
): {
  groupedValues?: FilterValueCategory[];
  ungroupedFilters?: FilterItem[];
} => {
  if (!valueCategories.length) {
    return {};
  }

  const groupedValues: FilterValueCategory[] = [];

  valueCategories.forEach((category, index) => {
    const categoryId = parentPath
      ? `${parentPath}>${category.label}-${index}`
      : `${category.label}-${index}`;

    // Get direct filters matching this category
    const directFilters = filters.filter(
      (item) =>
        !assignedFilterIds.has(item.id) &&
        matchesFacetValueCategory(item, category),
    );

    directFilters.forEach((item) => assignedFilterIds.add(item.id));

    // Recursively process children if present
    let processedChildren: FilterValueCategory[] | undefined;
    let childCount = 0;

    if (category.children?.length) {
      const childResult = groupFacetValues(
        filters,
        category.children,
        assignedFilterIds,
        categoryId,
      );
      if (childResult.groupedValues?.length) {
        processedChildren = childResult.groupedValues;
        childCount = processedChildren.reduce(
          (total, child) => total + child.count,
          0,
        );
      }
    }

    const directCount = directFilters.reduce(
      (total, item) => total + (item.count || 0),
      0,
    );
    const totalCount = directCount + childCount;

    // Only add category if it has filters or children with filters
    if (directFilters.length || processedChildren?.length) {
      groupedValues.push({
        id: categoryId,
        label: category.label,
        count: totalCount,
        filters: directFilters,
        ...(processedChildren?.length ? { children: processedChildren } : {}),
      });
    }
  });

  const ungroupedFilters = filters.filter(
    (item) => !assignedFilterIds.has(item.id),
  );

  return {
    ...(groupedValues.length ? { groupedValues } : {}),
    ...(ungroupedFilters.length ? { ungroupedFilters } : {}),
  };
};

export const parseTreeValue = (
  raw: string,
): {
  depth: number;
  label: string;
  parentPath: string | null;
  hierarchyPath: string;
} => {
  const parts = raw
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean);
  const depth = parseInt(parts[0], 10);
  const segments = parts.slice(1);
  const label = segments[segments.length - 1];
  const parentPath =
    depth > 1 ? `${depth - 1}/${segments.slice(0, -1).join('/')}` : null;
  return { depth, label, parentPath, hierarchyPath: segments.join('/') };
};

export const normalizeHierarchyTreeBase = (
  hierarchyTreeBase?: string,
): string | null => {
  if (!hierarchyTreeBase) {
    return null;
  }

  const parts = hierarchyTreeBase
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) {
    return null;
  }

  return /^\d+$/.test(parts[0]) ? parts.slice(1).join('/') : parts.join('/');
};

export const buildTreeNsdiFilterIdFromHierarchyBase = (
  hierarchyTreeBase?: string,
): string | null => {
  const normalizedHierarchyTreeBase =
    normalizeHierarchyTreeBase(hierarchyTreeBase);

  if (!normalizedHierarchyTreeBase) {
    return null;
  }

  const depth = normalizedHierarchyTreeBase.split('/').filter(Boolean).length;

  return `${depth}/${normalizedHierarchyTreeBase}`;
};

export const filterTreeNsdiFiltersByHierarchyBase = (
  filters: FilterItem[],
  hierarchyTreeBase?: string,
): FilterItem[] => {
  const normalizedHierarchyTreeBase =
    normalizeHierarchyTreeBase(hierarchyTreeBase);

  if (!normalizedHierarchyTreeBase) {
    return filters;
  }

  return filters.filter((filter) => {
    const { hierarchyPath } = parseTreeValue(filter.id);
    return (
      hierarchyPath === normalizedHierarchyTreeBase ||
      hierarchyPath.startsWith(`${normalizedHierarchyTreeBase}/`)
    );
  });
};

const findTreeNodeByHierarchyPath = (
  nodes: FilterValueCategory[],
  hierarchyPath: string,
): FilterValueCategory | null => {
  for (const node of nodes) {
    if (parseTreeValue(node.id).hierarchyPath === hierarchyPath) {
      return node;
    }

    const childMatch = findTreeNodeByHierarchyPath(
      node.children || [],
      hierarchyPath,
    );
    if (childMatch) {
      return childMatch;
    }
  }

  return null;
};

export const buildTreeNsdiValueCategories = (
  filters: FilterItem[],
): FilterValueCategory[] => {
  const nodeMap = new Map<string, FilterValueCategory>();
  const roots: FilterValueCategory[] = [];

  // Sort deepest nodes first. When we encounter a node ID for the first time,
  // nodeMap.has(id) tells us whether a deeper node already created it as a
  // parent placeholder — i.e., whether it is a non-leaf.
  const sorted = [...filters].sort(
    (a, b) => parseTreeValue(b.id).depth - parseTreeValue(a.id).depth,
  );

  sorted.forEach((filter) => {
    const { depth, label, parentPath } = parseTreeValue(filter.id);
    const isLeaf = !nodeMap.has(filter.id);

    if (isLeaf) {
      if (depth === 1 || parentPath === null) {
        // Root-level leaf: rare in practice for NSDI data, skip to avoid
        // the duplicate-label rendering problem.
        return;
      }
      // Ensure the parent node exists in the map
      if (!nodeMap.has(parentPath)) {
        const { label: parentLabel } = parseTreeValue(parentPath);
        nodeMap.set(parentPath, {
          id: parentPath,
          label: parentLabel,
          count: 0,
          filters: [],
          children: [],
        });
      }
      // Add this leaf directly as a selectable FilterItem on the parent
      nodeMap.get(parentPath)!.filters.push({
        sectionId: 'tree_nsdi',
        id: filter.id,
        label,
        count: filter.count ?? 0,
        isChecked: false,
      });
    } else {
      // Non-leaf: fill in the placeholder that a child already created
      const node = nodeMap.get(filter.id)!;
      node.label = label;
      node.count = filter.count ?? 0;
      node.filter = {
        sectionId: 'tree_nsdi',
        id: filter.id,
        label,
        count: filter.count ?? 0,
        isChecked: false,
      };

      if (depth === 1 || parentPath === null) {
        roots.push(node);
      } else {
        if (!nodeMap.has(parentPath)) {
          const { label: parentLabel } = parseTreeValue(parentPath);
          nodeMap.set(parentPath, {
            id: parentPath,
            label: parentLabel,
            count: 0,
            filters: [],
            children: [],
          });
        }
        nodeMap.get(parentPath)!.children!.push(node);
      }
    }
  });

  return roots;
};

export const buildTreeNsdiHierarchyView = (
  filters: FilterItem[],
  hierarchyTreeBase?: string,
): TreeNsdiHierarchyView => {
  const valueCategories = buildTreeNsdiValueCategories(filters);
  const normalizedHierarchyTreeBase =
    normalizeHierarchyTreeBase(hierarchyTreeBase);

  if (!normalizedHierarchyTreeBase) {
    return { valueCategories, filters };
  }

  const configuredRoot = findTreeNodeByHierarchyPath(
    valueCategories,
    normalizedHierarchyTreeBase,
  );

  if (!configuredRoot) {
    const configuredBaseFilter = filters.find(
      (filter) =>
        parseTreeValue(filter.id).hierarchyPath === normalizedHierarchyTreeBase,
    );
    const hasDescendants = filters.some((filter) =>
      parseTreeValue(filter.id).hierarchyPath.startsWith(
        `${normalizedHierarchyTreeBase}/`,
      ),
    );

    if (configuredBaseFilter && !hasDescendants) {
      return {
        valueCategories: [],
        filters: [],
        matchedHierarchyPath: normalizedHierarchyTreeBase,
        emptyMessage: 'No additional filters available.',
      };
    }

    return { valueCategories, filters };
  }

  const hierarchyFilters = filterTreeNsdiFiltersByHierarchyBase(
    filters,
    normalizedHierarchyTreeBase,
  ).filter(
    (filter) =>
      parseTreeValue(filter.id).hierarchyPath !== normalizedHierarchyTreeBase,
  );

  return {
    valueCategories: configuredRoot.children || [],
    filters: hierarchyFilters,
    ...(configuredRoot.filters.length
      ? { ungroupedFilters: configuredRoot.filters }
      : {}),
    matchedHierarchyPath: normalizedHierarchyTreeBase,
  };
};
